
import React, { useState } from 'react';
import { Integration, IntegrationDefinition, ProviderType } from '../types.ts';
import { PROVIDER_ICONS } from '../constants.ts';
import { X, CheckCircle2, ShieldAlert, Zap, Lock, Calendar, AlertTriangle, Clock, Loader2 } from 'lucide-react';

interface IntegrationDetailsProps {
  definition: IntegrationDefinition;
  integration?: Integration; // If connected
  ruleCount: number;
  impactedRules?: { id: string; name: string }[];
  isExternallyDisconnected?: boolean;
  isDisconnecting?: boolean;
  onClose: () => void;
  onConnect: () => void;
  onDisconnect: () => void;
  isComingSoon?: boolean;
}

export const IntegrationDetails: React.FC<IntegrationDetailsProps> = ({
  definition,
  integration,
  ruleCount,
  impactedRules = [],
  isExternallyDisconnected = false,
  isDisconnecting = false,
  onClose,
  onConnect,
  onDisconnect,
  isComingSoon = false
}) => {
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const Icon = PROVIDER_ICONS[definition.provider];
  const isConnected = !!integration && integration.isConnected;

  const handleDisconnectClick = () => {
    if (ruleCount > 0) {
      setShowDisconnectConfirm(true);
    } else {
      onDisconnect();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#0a0a0a]/60 backdrop-blur-sm" onClick={onClose} />

      <div className="bg-white w-full max-w-lg rounded-2xl nb-shadow-lg border-2 border-[#0a0a0a] relative z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">

        {/* Header */}
        <div className="p-6 md:p-8 border-b border-[#e4e0d2] flex items-start justify-between bg-[#f4f1e6]">
           <div className="flex items-center gap-5">
             <div className={`w-16 h-16 rounded-2xl flex items-center justify-center border border-[#e4e0d2] bg-white shrink-0`}>
                <Icon size={36} className={isConnected ? "text-[#0a0a0a]" : "text-[#4f4f48]"} />
             </div>
             <div>
                <h2 className="text-2xl font-bold text-[#0a0a0a]">{definition.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  {isComingSoon ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#fef3c7] text-[#92600a]">
                      <Clock size={12} strokeWidth={3} /> Coming Soon
                    </span>
                  ) : isConnected ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#dcfce7] text-[#15803d]">
                      <CheckCircle2 size={12} strokeWidth={3} /> Connected
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#ece8db] text-[#6f6b60]">
                      Not Connected
                    </span>
                  )}
                </div>
             </div>
           </div>
           <button onClick={onClose} className="text-[#4f4f48] hover:text-[#0a0a0a] p-1 hover:bg-[#ece8db] rounded-lg transition-colors">
             <X size={24} />
           </button>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 max-h-[60vh] overflow-y-auto">
          
          {showDisconnectConfirm ? (
            <div className="bg-[#ffe4e6] border border-[#fecdd3] rounded-xl p-6 text-center animate-in slide-in-from-bottom-4">
               <div className="w-14 h-14 bg-[#ffe4e6] text-[#be123c] rounded-full flex items-center justify-center mx-auto mb-4 border border-[#fecdd3]">
                 <AlertTriangle size={28} strokeWidth={2.5} />
               </div>
               <h3 className="text-xl font-bold text-[#9f1239] mb-2">Warning: Active Rules</h3>
               <p className="text-[#be123c] font-medium mb-4">
                 Disconnecting this integration will delete <strong>{ruleCount} automation rule{ruleCount !== 1 ? 's' : ''}</strong> that depend on it. This action cannot be undone.
               </p>
               {impactedRules.length > 0 && (
                 <div className="bg-white/60 border border-[#fecdd3] rounded-lg p-3 mb-6 max-h-32 overflow-y-auto text-left">
                   <p className="text-xs font-bold text-[#9f1239] uppercase tracking-wide mb-2">Affected Rules:</p>
                   <ul className="space-y-1">
                     {impactedRules.map((rule) => (
                       <li key={rule.id} className="text-sm text-[#be123c] font-medium truncate">
                         • {rule.name}
                       </li>
                     ))}
                   </ul>
                 </div>
               )}
               <div className="flex gap-3 justify-center">
                 <button
                   onClick={() => setShowDisconnectConfirm(false)}
                   disabled={isDisconnecting}
                   className="press border-2 border-[#0a0a0a] bg-white text-[#0a0a0a] px-5 py-2.5 rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   Cancel
                 </button>
                 <button
                   onClick={onDisconnect}
                   disabled={isDisconnecting}
                   className="press border-2 border-[#0a0a0a] bg-[#fb7185] text-[#0a0a0a] px-5 py-2.5 rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                 >
                   {isDisconnecting ? (
                     <>
                       <Loader2 size={16} className="animate-spin" />
                       Disconnecting...
                     </>
                   ) : (
                     'Yes, Disconnect'
                   )}
                 </button>
               </div>
            </div>
          ) : (
            <>
              {isComingSoon ? (
                <div className="space-y-6">
                  <div className="bg-[#fef3c7] border border-[#fde68a] rounded-xl p-6 text-center">
                    <div className="w-14 h-14 bg-[#fef3c7] text-[#92600a] rounded-full flex items-center justify-center mx-auto mb-4 border border-[#fde68a]">
                      <Clock size={28} strokeWidth={2.5} />
                    </div>
                    <h3 className="text-xl font-bold text-[#7c4a03] mb-2">Coming Soon</h3>
                    <p className="text-[#92600a] font-medium">
                      This integration is not yet available. We're working hard to bring you support for {definition.name}!
                    </p>
                  </div>

                  <p className="text-lg text-[#4f4f48] font-medium leading-relaxed">
                    {definition.description}
                  </p>

                  <div className="space-y-3">
                    <h3 className="text-sm font-bold text-[#4f4f48] uppercase tracking-wide">Planned Features</h3>
                    <ul className="grid grid-cols-1 gap-3">
                      {definition.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-3 bg-[#f4f1e6] p-3 rounded-xl border border-[#e4e0d2] opacity-60">
                           <div className="bg-[#ece8db] p-1 rounded-full shrink-0">
                             <CheckCircle2 size={14} className="text-[#6f6b60]" strokeWidth={3} />
                           </div>
                           <span className="text-sm font-bold text-[#4f4f48]">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : isConnected && integration ? (
                <div className="space-y-6">
                  {/* Externally Disconnected Warning */}
                  {isExternallyDisconnected && (
                    <div className="bg-[#fef3c7] border border-[#fde68a] rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="text-[#92600a] shrink-0 mt-0.5" size={20} />
                        <div>
                          <h4 className="font-bold text-[#7c4a03]">Integration Externally Disconnected</h4>
                          <p className="text-sm text-[#92600a] mt-1">
                            This integration was uninstalled from {definition.name}.
                            Click "Disconnect" below to clean up and remove the local configuration.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Connection Details */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-bold text-[#4f4f48] uppercase tracking-wide">Connection Details</h3>
                    <div className="bg-white border border-[#e4e0d2] rounded-xl p-4 space-y-3">
                       {integration.config?.account && (
                         <div className="flex items-center justify-between border-b border-[#e4e0d2] pb-2">
                           <span className="text-[#4f4f48] font-medium text-sm">Account / Org</span>
                           <span className="font-bold text-[#0a0a0a]">{integration.config.account}</span>
                         </div>
                       )}
                       {integration.config?.workspace && (
                         <div className="flex items-center justify-between border-b border-[#e4e0d2] pb-2">
                           <span className="text-[#4f4f48] font-medium text-sm">Workspace</span>
                           <span className="font-bold text-[#0a0a0a]">{integration.config.workspace}</span>
                         </div>
                       )}
                       <div className="flex items-center justify-between">
                         <span className="text-[#4f4f48] font-medium text-sm">Connected Since</span>
                         <span className="font-bold text-[#0a0a0a] flex items-center gap-1.5">
                           <Calendar size={14} className="text-[#4f4f48]" />
                           {new Date(integration.connectedAt || '').toLocaleDateString()}
                         </span>
                       </div>
                    </div>
                  </div>

                  {/* Permissions */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-bold text-[#4f4f48] uppercase tracking-wide">Permissions</h3>
                    <div className="bg-[#f4f1e6] border border-[#e4e0d2] rounded-xl p-4">
                       <ul className="grid grid-cols-1 gap-2">
                         {integration.config?.scopes?.map((scope: string) => (
                           <li key={scope} className="flex items-center gap-2 text-sm font-bold text-[#4f4f48]">
                             <Lock size={14} className="text-[#7e22ce]" /> {scope}
                           </li>
                         )) || (
                           <li className="text-sm text-[#4f4f48] italic">Standard access permissions granted.</li>
                         )}
                       </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <p className="text-lg text-[#4f4f48] font-medium leading-relaxed">
                    {definition.description}
                  </p>

                  <div className="space-y-3">
                    <h3 className="text-sm font-bold text-[#4f4f48] uppercase tracking-wide">Key Features</h3>
                    <ul className="grid grid-cols-1 gap-3">
                      {definition.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-3 bg-[#f4f1e6] p-3 rounded-xl border border-[#e4e0d2]">
                           <div className="bg-[#dcfce7] p-1 rounded-full shrink-0">
                             <CheckCircle2 size={14} className="text-[#15803d]" strokeWidth={3} />
                           </div>
                           <span className="text-sm font-bold text-[#0a0a0a]">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer Actions */}
        {!showDisconnectConfirm && (
          <div className="p-6 md:p-8 border-t border-[#e4e0d2] bg-[#f4f1e6] flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-5 py-3 rounded-xl font-bold border border-transparent bg-transparent text-[#4f4f48] hover:bg-[#ece8db] hover:text-[#0a0a0a] transition-all"
            >
              Close
            </button>

            {isComingSoon ? (
              <button
                disabled
                className="press border-2 border-[#0a0a0a] bg-white text-[#0a0a0a] px-8 py-3 rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Clock size={18} /> Not Available Yet
              </button>
            ) : isConnected ? (
              <button
                onClick={handleDisconnectClick}
                disabled={isDisconnecting}
                className="press border-2 border-[#0a0a0a] bg-[#fb7185] text-[#0a0a0a] px-5 py-3 rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDisconnecting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Disconnecting...
                  </>
                ) : (
                  'Disconnect'
                )}
              </button>
            ) : (
              <button
                onClick={onConnect}
                className="press border-2 border-[#0a0a0a] bg-[#22c55e] text-[#0a0a0a] px-8 py-3 rounded-xl font-bold transition-all flex items-center gap-2"
              >
                <Zap size={18} fill="currentColor" /> Connect Integration
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
};
